// Iter 391 (Renzo) — Nómina menor TEF a proveedores: proveedor + documento + monto → TXT Banco de Chile (mismas reglas CxP).
import { useEffect, useState } from 'react';
import { FileText, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { NumberInput } from '../NumberInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ProveedorCombobox } from '../compras/ProveedorCombobox';
import { descargarTxtB64 } from './NominaExcelDialog';
import { hoyChile } from './EgresoAlegraNominaFields';

const CLP = (v) => `$${Math.round(v || 0).toLocaleString('es-CL')}`;
const TIPOS = ['Factura', 'Boleta', 'Nota de crédito', 'Otro'];
const lineaVacia = () => ({ key: Math.random().toString(36).slice(2), proveedor: null, tipo_doc: 'Factura', folio: '', monto: '', concepto: '' });

export function NominaMenorDialog({ open, onClose, onGenerated }) {
  const [lineas, setLineas] = useState([lineaVacia()]);
  const [motivo, setMotivo] = useState('');
  const [generando, setGenerando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [crearEgresos, setCrearEgresos] = useState(true);
  const [fechaEgresos, setFechaEgresos] = useState(hoyChile());

  useEffect(() => { if (open) { setLineas([lineaVacia()]); setMotivo(''); setResultado(null); setCrearEgresos(true); setFechaEgresos(hoyChile()); } }, [open]);

  const upd = (key, patch) => setLineas((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const total = lineas.reduce((s, l) => s + (Number(l.monto) || 0), 0);
  const validas = lineas.filter((l) => l.proveedor && Number(l.monto) > 0);

  const generar = async () => {
    if (!validas.length) { toast.error('Agrega al menos una línea con proveedor y monto > 0'); return; }
    setGenerando(true);
    try {
      const r = await api.post('/tef/nomina-menor/generar', {
        motivo, crear_egresos_alegra: crearEgresos, fecha_egresos: fechaEgresos,
        lineas: validas.map((l) => ({ rut_proveedor: l.proveedor.rut, nombre: l.proveedor.nombre, tipo_doc: l.tipo_doc, folio: l.folio.trim(), monto: Math.round(Number(l.monto)), concepto: l.concepto })),
      });
      const d = r.data || {};
      setResultado(d);
      if (!d.archivos?.length) { toast.warning(d.mensaje || 'Nada por generar'); return; }
      d.archivos.forEach(descargarTxtB64);
      toast.success(d.mensaje, { duration: 8000 });
      if (d.egresos_alegra?.errores) toast.warning(`${d.egresos_alegra.errores} documento(s) sin egreso en Alegra: revisa el detalle y reintenta desde el Historial`, { duration: 10000 });
      onGenerated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error generando la nómina menor', { duration: 8000 });
    } finally { setGenerando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="dialog-nomina-menor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-emerald-700" /> Nómina menor TEF · Proveedores</DialogTitle>
          <DialogDescription>Pagos sueltos a proveedores con los datos bancarios de "Cuentas". Mismas reglas del banco: TEC/TOB, fraccionamiento sobre $5.000.000 en otros bancos, máx. 30 transferencias por archivo. En boletas de honorarios indica el líquido a transferir.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="grid grid-cols-12 gap-2 text-[11px] font-medium text-muted-foreground px-1">
            <div className="col-span-4">Proveedor (RUT o nombre)</div><div className="col-span-2">Tipo</div><div className="col-span-1">Folio</div><div className="col-span-2 text-right">Monto</div><div className="col-span-2">Concepto (opcional)</div><div />
          </div>
          <div className="max-h-[48vh] overflow-y-auto space-y-2 pr-1">
            {lineas.map((l, i) => <LineaNomina key={l.key} l={l} idx={i} upd={upd} quitar={() => setLineas((ls) => ls.filter((x) => x.key !== l.key))} puedeQuitar={lineas.length > 1} />)}
          </div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => setLineas((ls) => [...ls, lineaVacia()])} className="gap-1" data-testid="btn-agregar-linea"><Plus className="h-3.5 w-3.5" /> Agregar línea</Button>
            <div className="flex items-center gap-2">
              <Input placeholder="Motivo general (máx 30) · ej. Chirino Steel Pago Factura" value={motivo} onChange={(e) => setMotivo(e.target.value.slice(0, 30))} className="h-8 w-80 text-xs" data-testid="input-motivo-nomina-menor" />
              <span className="text-sm font-semibold tabular-nums" data-testid="nomina-menor-total">Total {CLP(total)} · {validas.length} transf.</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-sky-200 bg-sky-50/60 p-2 text-xs" data-testid="nomina-menor-egresos-bloque">
            <label className="flex items-center gap-2 font-medium cursor-pointer">
              <input type="checkbox" checked={crearEgresos} onChange={(e) => setCrearEgresos(e.target.checked)} data-testid="nomina-menor-crear-egresos" />
              Registrar en Alegra un Comprobante de Egreso por documento (aplicado a su factura/boleta)
            </label>
            {crearEgresos && (
              <label className="flex items-center gap-1">Fecha <Input type="date" value={fechaEgresos} onChange={(e) => setFechaEgresos(e.target.value)} className="h-7 w-36 text-xs bg-white" data-testid="nomina-menor-fecha-egresos" /></label>
            )}
            <span className="text-muted-foreground">Las líneas sin documento en Alegra (folio no encontrado, ya pagado o monto mayor al saldo) quedan con error y se reintentan desde el Historial.</span>
          </div>
          {resultado && (
            <div className="rounded border bg-slate-50 p-2 text-xs space-y-1" data-testid="nomina-menor-resultado">
              <p className="font-medium">{resultado.mensaje}</p>
              {resultado.incluidas?.map((x, i) => (
                <p key={i} className="text-emerald-800">
                  ✓ {x.tipo_op} · {x.nombre} · {x.asunto} · {CLP(x.monto)} → cta {x.cuenta_beneficiario}
                  {x.egreso_alegra?.estado === 'creado' && <span className="ml-1 text-sky-700">· Egreso Alegra N°{x.egreso_alegra.number || x.egreso_alegra.payment_id}</span>}
                  {x.egreso_alegra?.estado === 'error' && <span className="ml-1 text-red-700">· Sin egreso: {x.egreso_alegra.error}</span>}
                </p>
              ))}
              {resultado.omitidas?.map((x, i) => <p key={i} className="text-amber-800">⚠ {x.nombre || x.rut_proveedor} · {x.folio}: {x.motivo}</p>)}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={generando}>Cerrar</Button>
          <Button onClick={generar} disabled={generando || !validas.length} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="btn-generar-nomina-menor">
            {generando ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Generar TEF ({validas.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LineaNomina({ l, idx, upd, quitar, puedeQuitar }) {
  const [docs, setDocs] = useState([]);
  const rut = l.proveedor?.rut;

  useEffect(() => {
    if (!rut) { setDocs([]); return; }
    api.get('/tef/nomina-menor/documentos', { params: { rut } }).then((r) => setDocs(r.data.documentos || [])).catch(() => setDocs([]));
  }, [rut]);

  const elegirDoc = (id) => {
    const d = docs.find((x) => String(x.bill_id) === id);
    if (d) upd(l.key, { folio: d.numero, tipo_doc: 'Factura', monto: String(d.saldo > 0 ? d.saldo : d.total) });
  };

  return (
    <div className="grid grid-cols-12 gap-2 items-start rounded border p-2 bg-white" data-testid={`nomina-linea-${idx}`}>
      <div className="col-span-4 space-y-1">
        <ProveedorCombobox value={rut || ''} onChange={(p) => upd(l.key, { proveedor: p })} testid={`linea-${idx}-proveedor`} />
        {docs.length > 0 && (
          <Select onValueChange={elegirDoc}>
            <SelectTrigger className="h-7 text-[11px]" data-testid={`linea-${idx}-docs`}><SelectValue placeholder={`Autocompletar desde ${docs.length} documento(s) recientes…`} /></SelectTrigger>
            <SelectContent>
              {docs.map((d) => (
                <SelectItem key={d.bill_id} value={String(d.bill_id)} className="text-xs">
                  N° {d.numero} · {fmtFecha(d.fecha)} · {CLP(d.total)}{d.saldo > 0 && d.saldo !== d.total ? ` (saldo ${CLP(d.saldo)})` : ''} · {d.estado === 'open' ? 'pendiente' : 'pagada en Alegra'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="col-span-2">
        <Select value={l.tipo_doc} onValueChange={(v) => upd(l.key, { tipo_doc: v })}>
          <SelectTrigger className="h-9 text-xs" data-testid={`linea-${idx}-tipo`}><SelectValue /></SelectTrigger>
          <SelectContent>{TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="col-span-1"><Input value={l.folio} onChange={(e) => upd(l.key, { folio: e.target.value })} placeholder="N°" className="h-9 text-xs font-mono" data-testid={`linea-${idx}-folio`} /></div>
      <div className="col-span-2"><NumberInput value={l.monto} onChange={(v) => upd(l.key, { monto: v })} placeholder="0" className="h-9 text-xs text-right tabular-nums" data-testid={`linea-${idx}-monto`} /></div>
      <div className="col-span-2"><Input value={l.concepto} onChange={(e) => upd(l.key, { concepto: e.target.value.slice(0, 30) })} placeholder="Motivo línea" className="h-9 text-xs" data-testid={`linea-${idx}-concepto`} /></div>
      <div className="col-span-1 flex justify-end">
        <Button type="button" size="icon" variant="ghost" onClick={quitar} disabled={!puedeQuitar} className="h-9 w-9 text-red-600" data-testid={`linea-${idx}-quitar`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
